import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import {
	LogisticsApiService,
	VehicleRate,
	SetRateBody,
	Booking,
	CreateBookingBody,
	DailyCostRow,
	WeeklyCost,
} from '../../../services/logisticsApi';

const todayIso = () => {
	const n = new Date();
	return new Date(n.getTime() - n.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
};

/** Active vehicle rate as-of a date (defaults today). Enabled only with a vehicle id. */
export const useVehicleRate = (vehicleId?: number, asOf?: string): UseQueryResult<VehicleRate | null, Error> =>
	useQuery({
		queryKey: ['logistics', 'rate', vehicleId, asOf ?? todayIso()],
		queryFn: () => LogisticsApiService.getRate(vehicleId as number, asOf ?? todayIso()),
		enabled: !!vehicleId,
		staleTime: 0,
		refetchOnWindowFocus: false,
	});

export const useSetRate = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ vehicleId, body }: { vehicleId: number; body: SetRateBody }) =>
			LogisticsApiService.setRate(vehicleId, body),
		onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['logistics', 'rate', v.vehicleId] }),
	});
};

/** Bookings for a facility within a date range. */
export const useBookings = (
	facilityId?: number,
	from?: string,
	to?: string
): UseQueryResult<Booking[], Error> =>
	useQuery({
		queryKey: ['logistics', 'bookings', facilityId, from, to],
		queryFn: () => LogisticsApiService.listBookings(facilityId as number, from as string, to as string),
		enabled: !!facilityId && !!from && !!to,
		staleTime: 0,
		refetchOnWindowFocus: false,
	});

export const useCreateBooking = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: CreateBookingBody) => LogisticsApiService.createBooking(body),
		onSuccess: () => {
			// Booking cost flows into the facility's logistics cost + EBITDA seam immediately.
			qc.invalidateQueries({ queryKey: ['logistics', 'bookings'] });
			qc.invalidateQueries({ queryKey: ['logistics', 'cost'] });
		},
	});
};

export const useDailyCost = (facilityId?: number, date?: string): UseQueryResult<DailyCostRow[], Error> =>
	useQuery({
		queryKey: ['logistics', 'cost', 'daily', facilityId, date],
		queryFn: () => LogisticsApiService.costDaily(facilityId as number, date as string),
		enabled: !!facilityId && !!date,
		staleTime: 0,
		refetchOnWindowFocus: false,
	});

export const useWeeklyCost = (facilityId?: number, month?: string): UseQueryResult<WeeklyCost | null, Error> =>
	useQuery({
		queryKey: ['logistics', 'cost', 'weekly', facilityId, month],
		queryFn: () => LogisticsApiService.costWeekly(facilityId as number, month as string),
		enabled: !!facilityId && !!month,
		staleTime: 0,
		refetchOnWindowFocus: false,
	});

export const useAccrue = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ facilityId, month }: { facilityId: number; month: string }) =>
			LogisticsApiService.accrue(facilityId, month),
		onSuccess: () => qc.invalidateQueries({ queryKey: ['logistics', 'cost'] }),
	});
};
